<template>
  <b-modal
    :id="modalId"
    size="md"
    title="รายละเอียด"
    header-class="py-4 px-6 border-bottom"
    body-class="px-6 py-5"
    footer-class="py-3 px-6 border-top d-flex justify-content-end"
    scrollable
  >
    <div v-if="batch" class="font-size-sm">
      <div class="mb-5">
        <span class="font-weight-bolder text-dark font-size-h6 d-block">{{ sourceDisplay.text }}</span>
        <span class="text-muted font-weight-bold">
          {{ batch.reference_code || batch.id }} · {{ formatThaiDateTime(batch.received_at) }}
        </span>
      </div>

      <h6 class="font-weight-bolder mb-3">ข้อมูลที่มา</h6>

      <template v-if="hasReceiptEvidence">
        <div
          v-if="source.receipt_image"
          class="bg-light rounded text-center mb-4 receipt-image-frame"
        >
          <img :src="source.receipt_image" alt="receipt_image" class="receipt-image" />
        </div>
        <div v-else class="bg-light rounded text-center text-muted py-8 mb-4">
          ไม่มีรูปใบเสร็จแนบมา
        </div>
      </template>

      <template v-if="source">
        <div
          v-for="detail in sourceDetails"
          :key="detail.label"
          class="d-flex justify-content-between align-items-start pb-3 border-bottom mb-3"
        >
          <span class="text-muted font-weight-bold mr-3">{{ detail.label }}</span>
          <span class="text-dark text-right font-weight-bold detail-value">{{ detail.value }}</span>
        </div>
      </template>
      <p v-else class="text-muted mb-3">ไม่พบข้อมูลต้นทางของแต้มก้อนนี้</p>

      <h6 class="font-weight-bolder mt-6 mb-3">สรุปแต้ม</h6>
      <div
        v-for="row in pointRows"
        :key="row.label"
        class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3"
      >
        <span class="text-muted font-weight-bold">{{ row.label }}</span>
        <span class="font-weight-bold" :class="row.valueClass">{{ row.value }}</span>
      </div>
      <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
        <span class="text-muted font-weight-bold">วันหมดอายุ</span>
        <span class="text-dark font-weight-bold">
          {{ batch.expiration_date ? formatThaiDate(batch.expiration_date) : "ไม่มีวันหมดอายุ" }}
          <span v-if="batch.expiration_months" class="text-muted font-weight-normal">
            (อายุ {{ batch.expiration_months }} เดือน)
          </span>
        </span>
      </div>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <span class="text-muted font-weight-bold">สถานะ</span>
        <b-badge :variant="statusDisplay.variant">{{ statusDisplay.text }}</b-badge>
      </div>

      <h6 class="font-weight-bolder mt-6 mb-3">ความเคลื่อนไหว</h6>
      <p v-if="!traceLines.length" class="text-muted mb-0">ยังไม่มีการแลกหรือหมดอายุจากก้อนนี้</p>
      <div
        v-for="line in traceLines"
        :key="line.key"
        class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3"
      >
        <div>
          <b-badge :variant="transactionTypeDisplay[line.type].variant" class="mr-2">
            {{ transactionTypeDisplay[line.type].text }}
          </b-badge>
          <span class="text-muted">{{ line.transactionId }}</span>
          <span class="text-muted font-size-xs d-block mt-1">{{ formatThaiDateTime(line.createdAt) }}</span>
        </div>
        <span class="font-weight-bolder text-dark text-nowrap ml-3">{{ line.displayAmount }} แต้ม</span>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <b-button variant="light" class="font-weight-bold px-5" @click="cancel()">ปิด</b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BATCH_STATUS_DISPLAY,
  TRANSACTION_TYPE_DISPLAY,
  formatPoint,
  formatThaiDate,
  formatThaiDateTime,
  pointSourceDetails,
  pointSourceDisplay
} from "../point-expire.helper";

const RECEIPT_EVIDENCE_SOURCE_TYPES = ["receipt", "point_grant"];

export default {
  name: "PointExpireBatchDetailModal",
  props: {
    modalId: {
      type: String,
      required: true
    },
    batch: {
      type: Object,
      default: null
    },
    traceLines: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      transactionTypeDisplay: TRANSACTION_TYPE_DISPLAY
    };
  },
  computed: {
    source() {
      return this.batch ? this.batch.source : null;
    },
    sourceDisplay() {
      return pointSourceDisplay(this.batch.source_type);
    },
    sourceDetails() {
      return pointSourceDetails(this.source);
    },
    hasReceiptEvidence() {
      return Boolean(this.source) && RECEIPT_EVIDENCE_SOURCE_TYPES.includes(this.batch.source_type);
    },
    statusDisplay() {
      return BATCH_STATUS_DISPLAY[this.batch.status];
    },
    pointRows() {
      const rows = [
        { label: "ได้รับ", value: `${formatPoint(this.batch.earned_amount)} แต้ม`, valueClass: "text-success" },
        { label: "แลกไป", value: `${formatPoint(this.batch.redeemed_amount)} แต้ม`, valueClass: "text-dark" },
        {
          label: "หมดอายุไป",
          value: `${formatPoint(this.batch.expired_amount)} แต้ม`,
          valueClass: this.batch.expired_amount > 0 ? "text-danger" : "text-dark"
        }
      ];
      if (Number(this.batch.adjusted_amount)) {
        rows.push({ label: "ปรับลด", value: `${formatPoint(this.batch.adjusted_amount)} แต้ม`, valueClass: "text-warning" });
      }
      if (Number(this.batch.reversed_amount)) {
        rows.push({ label: "ยกเลิกรายการ", value: `${formatPoint(this.batch.reversed_amount)} แต้ม`, valueClass: "text-dark" });
      }
      rows.push({ label: "คงเหลือ", value: `${formatPoint(this.batch.remaining)} แต้ม`, valueClass: "text-primary font-weight-bolder" });
      return rows;
    }
  },
  methods: {
    formatThaiDate,
    formatThaiDateTime
  }
};
</script>

<style scoped>
.receipt-image-frame {
  overflow: hidden;
}

.receipt-image {
  max-width: 100%;
  max-height: 360px;
  object-fit: contain;
}

.detail-value {
  max-width: 60%;
  word-break: break-word;
}
</style>
